import { requestConfig, webApiEndpoints } from "../config";
import { mapper } from "../helpers/mapper.helper";
import secureHelper from "../helpers/secure.helper";
import { ApiJiraIssue, ApiJiraIssueItem, AppCategories, AppRequestType, CredentialJiraType, DashboardProject, DashboardTicketType, IssueType, IssueWorkflow, JiraIssue, NewCredentialType, PathAppOptions, PinnedDashboard, UserDocumentFile } from "../types";

const category: AppCategories = 'tickets'

export const ticketsAdapter = {

    getJiraCredentials: (): AppRequestType<CredentialJiraType[]>[typeof category]['credencialesJira'] => ({
        endpoint: webApiEndpoints[category].credencialesJira,
        input: null,
        output: (data) => data.map(mapper.apiCredentialsJira)
    }),

    addCredential: (data: NewCredentialType): AppRequestType<CredentialJiraType>[typeof category]['registraCredencial'] => ({
        endpoint: webApiEndpoints[category].registraCredencial,
        input: ({
            workspace: data.workspace,
            descripcion: data.description,
            acceso: data.key,
            uriRedirrecion: data.redirectUri,
            credencial: data.secret,
            codigo: data.initialCode
        }),
        output: (datos) => mapper.apiCredentialsJira(datos)
    }),

    editCredential: (data: NewCredentialType): AppRequestType<CredentialJiraType>[typeof category]['editaCredencial'] => ({
        endpoint: webApiEndpoints[category].editaCredencial,
        input: ({
            workspace: data.workspace,
            descripcion: data.description,
            acceso: data.key,
            credencial: data.secret,
            uriRedirrecion: data.redirectUri,
            codigo: data.initialCode
        }),
        output: (datos) => mapper.apiCredentialsJira(datos)
    }),

    deleteCredential: (data: CredentialJiraType): AppRequestType<number>[typeof category]['eliminaCredencial'] => ({
        endpoint: webApiEndpoints[category].eliminaCredencial,
        input: ({
            id: data.id,
            workspace: data.workspace,
            descripcion: '',
            acceso: '',
            credencial: '',
            repositorios: [],
            fechaControl: '',
            usuarioModificador: '',
            token: ''
        }),
        output: (datos) => datos
    }),

    getProjects: (idCredential: number): AppRequestType<DashboardProject[]>[typeof category]['listaProyectos'] => ({
        endpoint: webApiEndpoints[category].listaProyectos,
        input: null,
        parameters: { headers: { idResource: idCredential?.toString() } },
        output: (data) => data.map(mapper.apiJiraProject)
    }),

    getProjectTicketType: (idCredential: number, idDashboard?: string): AppRequestType<DashboardTicketType[]>[typeof category]['tiposTicket'] => ({
        endpoint: webApiEndpoints[category].tiposTicket,
        input: null,
        parameters: { headers: { idResource: idCredential?.toString(), tipo: idDashboard } },
        output: (data) => data.map(d => ({
            description: d.descripcion,
            id: d.id,
            name: d.nombre
        }))
    }),

    validateWorkflow: function (
        idCredential: number,
        idTicketType: string,
        idDashboard?: string
    ): AppRequestType<IssueWorkflow>[typeof category]['tipoTicketWorkflow'] {
        return {
            endpoint: webApiEndpoints[category].tipoTicketWorkflow,
            input: {
                descripcion: '',
                id: idTicketType,
                nombre: ''
            },
            parameters: { headers: { idResource: idCredential?.toString(), tipo: idDashboard } },
            output: (data) => mapper.apiJiraWorkflow(data)
        }
    },

    pinDashboard: (data: PinnedDashboard): AppRequestType<PinnedDashboard>[typeof category]['fijarDashboard'] => ({
        endpoint: webApiEndpoints[category].fijarDashboard,
        parameters: { headers: { idResource: requestConfig.baseURL } },
        input: ({
            id: 0,
            idCredencial: data.idCredential,
            avatar: data.avatar,
            idTercero: data.dashboardId,
            nombre: data.title,
            descripcion: data.description,
            tipo: data.ticketType,
            key: ''
        }),
        output: (datos) => mapper.apiPinnedDashboard(datos)
    }),


    unpinDashboard: (data: PinnedDashboard): AppRequestType<PinnedDashboard>[typeof category]['retirarFijado'] => ({
        endpoint: webApiEndpoints[category].retirarFijado,
        parameters: { headers: { idResource: requestConfig.baseURL } },
        input: ({
            id: data.id,
            idCredencial: 0,
            avatar: '',
            idTercero: data.dashboardId,
            nombre: data.title,
            descripcion: data.description,
            tipo: data.ticketType,
            key: ''
        }),
        output: (datos) => mapper.apiPinnedDashboard(datos)
    }),

    listIssues: (data: PinnedDashboard): AppRequestType<IssueWorkflow>[typeof category]['listarIssues'] => ({
        endpoint: webApiEndpoints[category].listarIssues,
        input: ({
            id: data.id,
            idCredencial: data.idCredential,
            avatar: '',
            idTercero: data.dashboardId,
            nombre: data.title,
            descripcion: data.description,
            tipo: data.ticketType,
            key: ''
        }),
        output: (data) => mapper.apiJiraWorkflow(data)
    }),

    sendIssue: (data: IssueType, files: File[]): AppRequestType<IssueWorkflow>[typeof category]['enviarIssue'] => {
        let input: ApiJiraIssue = {
            descripcion: data.description,
            idProyecto: data.projectId,
            resumen: data.summary
        };
        let formData = new FormData()
        files.forEach(f => {
            formData.append("files", f)
        })
        formData.append("input", secureHelper.encriptApiRequest(JSON.stringify(input), process.env.REACT_APP_CYPHER || ''))
        return ({
            endpoint: webApiEndpoints[category].enviarIssue,
            input: formData,
            output: (data) => mapper.apiJiraWorkflow(data)
        })
    },

    checkIssue: (idCredential: number, issueKey: string): AppRequestType<JiraIssue>[typeof category]['detalleIssue'] => {
        let pathVariables: Map<PathAppOptions, string> = new Map();
        pathVariables.set('{origin}', issueKey)
        return {
            endpoint: webApiEndpoints[category].detalleIssue,
            parameters: { pathVariables, headers: { idResource: idCredential?.toString() } },
            input: null,
            output: (data) => mapper.apiJiraIssue(data)
        }
    },

    addComment: (data: JiraIssue, files: File[]): AppRequestType<boolean>[typeof category]['comentaIssue'] => {
        let input: ApiJiraIssueItem = {
            descripcion: data.description,
            idProyecto: data.projectId,
            resumen: '',
            key: data.key,
            id: data.id
        };
        let formData = new FormData()
        files.forEach(f => {
            formData.append("files", f)
        })
        formData.append("input", secureHelper.encriptApiRequest(JSON.stringify(input), process.env.REACT_APP_CYPHER || ''))
        return ({
            endpoint: webApiEndpoints[category].comentaIssue,
            input: formData,
            output: (data) => {
                console.log(data)
                return true;
            }
        })
    }

}
